import { yupResolver } from "@hookform/resolvers/yup";
import { DialogActions, DialogContent, Grid2, Grid2Props, Stack } from "@mui/material";
import { ReactNode } from "react";
import { DefaultValues, FieldValues, FormProvider, Resolver, useForm, UseFormProps } from "react-hook-form";

import { extractInitialValuesFromRecord } from "../../helpers";
import { useFormDefaults } from "../../hooks";

import { ActionButtons } from "@/components/elements";
import { FieldSchema } from "@/components/section/interfaces";
import { useModalStore } from "@/hooks";

export type FormType = "create" | "edit" | "read-only";

export interface ReactHookFormProps<TFormValues extends FieldValues, TRecord = TFormValues, TPayload = TFormValues>
  extends UseFormProps<TFormValues> {
  data?: TRecord;
  onSubmit: (values: TPayload) => void;
  onCancel?: () => void;
  formSchema: FieldSchema<TFormValues, TRecord>[];
  view?: "page" | "modal";
  formType?: FormType;
  cancelText?: string;
  submitText?: string;
  disabled?: boolean;
  loading?: boolean;
  modifyInitialValues?: (formValues: TFormValues, record?: TRecord) => TFormValues;
  modifySubmissionValues?: (formValues: TFormValues, record?: TRecord) => TPayload;
  getFormActions?: (actions: { formActions: ReactNode }) => ReactNode;
  gridProps?: Grid2Props;
}

export function ReactHookForm<TFormValues extends FieldValues, TRecord = TFormValues, TPayload = TFormValues>({
  data,
  onSubmit,
  onCancel,
  getFormActions,
  formSchema,
  view = "page",
  formType = "create",
  cancelText = "Cancel",
  submitText = "Submit",
  disabled = false,
  loading = false,
  modifyInitialValues = (values) => values,
  modifySubmissionValues = (values) => values as unknown as TPayload,
  gridProps = {},
  ...options
}: ReactHookFormProps<TFormValues, TRecord, TPayload>) {
  const { popModal } = useModalStore((state) => state.actions);
  const { initialValues, validationSchema, elements } = useFormDefaults<TFormValues, TRecord>(formSchema, formType);

  const modifiedInitialValues = !data
    ? modifyInitialValues(initialValues)
    : modifyInitialValues(extractInitialValuesFromRecord(data, initialValues), data);

  const methods = useForm<TFormValues>({
    ...options,
    defaultValues: modifiedInitialValues as DefaultValues<TFormValues>,
    resolver: yupResolver(validationSchema) as Resolver<any>,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const handleCancel = () => {
    if (view === "modal") {
      popModal();
    }

    if (onCancel) {
      onCancel();
    }
  };

  const formContent = (
    <Grid2 container spacing={3} {...gridProps} sx={{ maxWidth: "100%" }}>
      {elements}
    </Grid2>
  );

  const formActions = (
    <ActionButtons
      type="form"
      onCancel={handleCancel}
      cancelText={cancelText}
      loading={loading || isSubmitting}
      disabled={disabled}
      confirmText={submitText}
    />
  );

  const actions = !getFormActions
    ? formActions
    : getFormActions({
        formActions,
      });

  const onFormSubmit = (values: TFormValues) => {
    onSubmit(modifySubmissionValues(values, data));
  };

  if (view === "page") {
    return (
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <Stack>
            {formContent}
            {actions}
          </Stack>
        </form>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogContent dividers>{formContent}</DialogContent>
        <DialogActions>{actions}</DialogActions>
      </form>
    </FormProvider>
  );
}
